/**
 * Duplicate Review Index Page
 *
 * Main page for reviewing transactions that were skipped during import
 * as potential duplicates. Users can review each match and either confirm
 * the skip or force import the transaction.
 *
 * Features:
 * - Filter chips to toggle between unreviewed and all duplicates
 * - List of DuplicateReviewRow components
 * - Bulk selection with action bar
 * - Empty state when no duplicates
 * - Toast notifications for actions
 */

import { useState, useCallback, useEffect } from 'react';
import { Head, Link, usePage, router } from '@inertiajs/react';
import { Toaster, toast } from 'sonner';
import { Copy, ArrowLeft, X, CheckCircle } from 'lucide-react';
import { Navbar } from '@/components';

import { useDuplicates } from '@/hooks/useDuplicates';
import { DuplicateReviewRow } from './components/DuplicateReviewRow';
import { DuplicateEmptyState } from './components/DuplicateEmptyState';
import type { DuplicatesPageProps, DuplicateFilter } from '@/types/accounting';

/**
 * DuplicateReviewIndex is the main page for reviewing duplicate transactions.
 */
export default function DuplicateReviewIndex() {
  // Get page props from Inertia
  const {
    duplicates: initialDuplicates = [],
    meta: initialMeta,
  } = usePage<DuplicatesPageProps>().props;

  // Get current filter from URL or default to 'unreviewed'
  const urlParams = new URLSearchParams(window.location.search);
  const currentFilter = (urlParams.get('filter') as DuplicateFilter) || 'unreviewed';

  // Selection state for bulk actions
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [processingId, setProcessingId] = useState<number | null>(null);

  const {
    duplicates,
    meta,
    loading,
    error,
    fetchDuplicates,
    markReviewed,
    forceImport,
    bulkMarkReviewed,
    clearError,
  } = useDuplicates();

  // Initialize duplicates from page props or fetch from API
  useEffect(() => {
    if (initialDuplicates.length > 0 || initialMeta) {
      // If we have initial data from Rails, we could use it
      // But for now, always fetch fresh data to ensure consistency
    }
    fetchDuplicates(currentFilter).catch(() => {
      // Error is handled by the hook
    });
  }, [fetchDuplicates, currentFilter]);

  // Clear selection when filter changes
  useEffect(() => {
    setSelectedIds(new Set());
  }, [currentFilter]);

  /**
   * Handle filter chip click - navigate with Inertia
   */
  const handleFilterChange = useCallback((filter: DuplicateFilter) => {
    router.visit(`/accounting/duplicates?filter=${filter}`, {
      preserveState: true,
      preserveScroll: true,
    });
  }, []);

  /**
   * Handle selection change for a single row
   */
  const handleSelectionChange = useCallback((id: number, selected: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (selected) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }, []);

  /**
   * Handle select all unreviewed duplicates
   */
  const handleSelectAll = useCallback(() => {
    const unreviewedIds = duplicates
      .filter(d => !d.duplicateReviewed)
      .map(d => d.id);
    setSelectedIds(new Set(unreviewedIds));
  }, [duplicates]);

  /**
   * Clear all selections
   */
  const handleClearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  /**
   * Handle mark as reviewed action for a single duplicate
   */
  const handleMarkReviewed = useCallback(async (id: number) => {
    setProcessingId(id);
    try {
      await markReviewed(id);
      toast.success('Marked as reviewed');
      // Remove from selection if it was selected
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to mark as reviewed');
    } finally {
      setProcessingId(null);
    }
  }, [markReviewed]);

  /**
   * Handle force import action for a single duplicate
   */
  const handleForceImport = useCallback(async (id: number) => {
    setProcessingId(id);
    try {
      const result = await forceImport(id);
      toast.success(
        `Imported as transaction #${result.transaction.id}`,
        {
          action: {
            label: 'View',
            onClick: () => router.visit(`/accounting/transactions/${result.transaction.id}`),
          },
        }
      );
      // Remove from selection if it was selected
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to import transaction');
    } finally {
      setProcessingId(null);
    }
  }, [forceImport]);

  /**
   * Handle bulk mark as reviewed action
   */
  const handleBulkMarkReviewed = useCallback(async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    setProcessingId(-1); // Use -1 to indicate bulk processing
    try {
      const result = await bulkMarkReviewed(ids);
      toast.success(`Marked ${result.updatedCount} duplicate${result.updatedCount !== 1 ? 's' : ''} as reviewed`);
      setSelectedIds(new Set());
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to mark as reviewed');
    } finally {
      setProcessingId(null);
    }
  }, [selectedIds, bulkMarkReviewed]);

  // Apply client-side filtering for optimistic updates
  // When a duplicate is marked as reviewed, it should disappear if filter is 'unreviewed'
  const filteredDuplicates = currentFilter === 'unreviewed'
    ? duplicates.filter(d => !d.duplicateReviewed)
    : duplicates;

  // Count unreviewed duplicates in current list
  const unreviewedInList = filteredDuplicates.filter(d => !d.duplicateReviewed).length;

  return (
    <>
      <Head title="Duplicate Review" />
      <Toaster position="top-right" richColors closeButton />

      <div className="min-h-screen bg-gray-50" data-testid="duplicate-review-page">
        <Navbar currentSection="accounting" />

        {/* Header */}
        <header className="bg-white border-b px-6 py-4">
          <div className="flex items-center justify-between max-w-6xl mx-auto">
            <div className="flex items-center gap-4">
              <Link
                href="/accounting/transactions"
                className="inline-flex items-center gap-1.5 px-2 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft size={16} />
                Transactions
              </Link>
              <div>
                <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
                  <Copy className="text-blue-500" size={24} />
                  Duplicate Review
                </h1>
                <p className="mt-1 text-sm text-gray-500">
                  Review transactions that were skipped during import as potential duplicates
                </p>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="px-6 py-6 max-w-6xl mx-auto">
          {/* Error State */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <svg
                className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <div>
                <h3 className="font-semibold text-red-800">Error Loading Duplicates</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
                <button
                  onClick={() => {
                    clearError();
                    fetchDuplicates(currentFilter);
                  }}
                  className="mt-2 text-sm font-medium text-red-700 hover:text-red-900 underline"
                >
                  Try again
                </button>
              </div>
            </div>
          )}

          {/* Quick Filters */}
          <div className="flex gap-2 mb-4" data-testid="filter-chips">
            <button
              type="button"
              onClick={() => handleFilterChange('unreviewed')}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                currentFilter === 'unreviewed'
                  ? 'bg-blue-100 text-blue-700 border-blue-300'
                  : 'bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-200'
              }`}
              data-testid="filter-unreviewed"
            >
              Unreviewed only
              <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                currentFilter === 'unreviewed'
                  ? 'bg-blue-200/50'
                  : 'bg-black/10'
              }`}>
                {meta?.unreviewedCount ?? 0}
              </span>
            </button>
            <button
              type="button"
              onClick={() => handleFilterChange('all')}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                currentFilter === 'all'
                  ? 'bg-blue-100 text-blue-700 border-blue-300'
                  : 'bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-200'
              }`}
              data-testid="filter-all"
            >
              All
              <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                currentFilter === 'all'
                  ? 'bg-blue-200/50'
                  : 'bg-black/10'
              }`}>
                {meta?.totalCount ?? 0}
              </span>
            </button>
          </div>

          {/* Loading State */}
          {loading && duplicates.length === 0 && (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="bg-white rounded-xl border border-gray-200 overflow-hidden animate-pulse"
                >
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-[18px] h-[18px] bg-gray-200 rounded" />
                        <div className="h-6 bg-gray-200 rounded w-24" />
                      </div>
                      <div className="h-6 bg-gray-200 rounded w-32" />
                    </div>
                  </div>
                  <div className="grid grid-cols-[1fr_auto_1fr]">
                    <div className="p-4 bg-amber-50/50">
                      <div className="h-4 bg-amber-100 rounded w-20 mb-3" />
                      <div className="space-y-2">
                        <div className="h-4 bg-gray-100 rounded w-full" />
                        <div className="h-4 bg-gray-100 rounded w-3/4" />
                        <div className="h-12 bg-gray-100 rounded w-full" />
                      </div>
                    </div>
                    <div className="px-3 py-4 bg-gray-100 border-x border-gray-200">
                      <div className="w-8 h-8 bg-gray-200 rounded-full" />
                    </div>
                    <div className="p-4 bg-green-50/50">
                      <div className="h-4 bg-green-100 rounded w-20 mb-3" />
                      <div className="space-y-2">
                        <div className="h-4 bg-gray-100 rounded w-full" />
                        <div className="h-4 bg-gray-100 rounded w-3/4" />
                        <div className="h-12 bg-gray-100 rounded w-full" />
                      </div>
                    </div>
                  </div>
                  <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
                    <div className="flex justify-end gap-2">
                      <div className="h-9 bg-gray-200 rounded w-32" />
                      <div className="h-9 bg-gray-200 rounded w-32" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && filteredDuplicates.length === 0 && (
            <DuplicateEmptyState filter={currentFilter} />
          )}

          {/* Duplicates List */}
          {!loading && filteredDuplicates.length > 0 && (
            <div className="space-y-4" data-testid="duplicates-list">
              {filteredDuplicates.map((duplicate) => (
                <DuplicateReviewRow
                  key={duplicate.id}
                  duplicate={duplicate}
                  isSelected={selectedIds.has(duplicate.id)}
                  isProcessing={processingId === duplicate.id || processingId === -1}
                  onSelectionChange={handleSelectionChange}
                  onMarkReviewed={handleMarkReviewed}
                  onForceImport={handleForceImport}
                />
              ))}
            </div>
          )}
        </main>

        {/* Bulk Actions Bar */}
        {selectedIds.size > 0 && (
          <div
            className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50"
            style={{ animation: 'slideUp 0.2s ease-out' }}
            data-testid="bulk-actions-bar"
          >
            <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle size={20} className="text-blue-500" />
                <span className="font-medium text-gray-900">
                  {selectedIds.size} selected
                </span>
                <span className="text-gray-500">
                  of {unreviewedInList}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  <CheckCircle size={14} />
                  Select All ({unreviewedInList})
                </button>
                <button
                  type="button"
                  onClick={handleBulkMarkReviewed}
                  disabled={processingId !== null}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <CheckCircle size={14} />
                  Mark All as Reviewed
                </button>
                <button
                  type="button"
                  onClick={handleClearSelection}
                  className="p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                  aria-label="Clear selection"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes slideUp {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }
      `}</style>
    </>
  );
}
